import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Popover,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  DragIndicator,
  Preview,
} from "@mui/icons-material";
import {
  MatchingConfig,
  MatchingQuestion,
  MatchingQuestionType,
} from "../../types";

// ── Question type metadata ─────────────────────────────────────────────────────

const QUESTION_TYPE_LABELS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]: "Time Availability",
  [MatchingQuestionType.RolePreference]: "Role Preference",
  [MatchingQuestionType.Personality]: "Personality Type",
  [MatchingQuestionType.PreferredTeammates]: "Preferred Teammates",
  [MatchingQuestionType.CustomText]: "Custom Question",
};

const QUESTION_TYPE_SUBLABELS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]:
    "Students pick available day/time slots from a 7×3 weekly grid",
  [MatchingQuestionType.RolePreference]:
    "Students select their preferred team role from a predefined list",
  [MatchingQuestionType.Personality]:
    "Students identify their MBTI personality type",
  [MatchingQuestionType.PreferredTeammates]:
    "Students list specific classmates they'd like to work with",
  [MatchingQuestionType.CustomText]:
    "A free-text or multiple-choice question you write yourself",
};

/** Chip colour per question type — makes chip visually distinct with good contrast */
const QUESTION_TYPE_CHIP_COLOR: Record<
  MatchingQuestionType,
  "default" | "info" | "secondary" | "success" | "warning"
> = {
  [MatchingQuestionType.TimeAvailability]: "info",
  [MatchingQuestionType.RolePreference]: "success",
  [MatchingQuestionType.Personality]: "secondary",
  [MatchingQuestionType.PreferredTeammates]: "warning",
  [MatchingQuestionType.CustomText]: "default",
};

/** Default affinity direction per question type */
const QUESTION_TYPE_DEFAULT_AFFINITY: Record<MatchingQuestionType, number> = {
  [MatchingQuestionType.TimeAvailability]: 1.0, // similarity — same schedule
  [MatchingQuestionType.RolePreference]: -1.0, // diversity — complementary roles
  [MatchingQuestionType.Personality]: -0.5, // lean diverse
  [MatchingQuestionType.PreferredTeammates]: 1.0, // similarity — honor preferences
  [MatchingQuestionType.CustomText]: 0.0, // neutral
};

/** Maximum number of matching criteria (questions) per config */
const MAX_CRITERIA = 4;

// Standard types that should only appear once
const SINGLETON_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.TimeAvailability,
  MatchingQuestionType.RolePreference,
  MatchingQuestionType.Personality,
  MatchingQuestionType.PreferredTeammates,
]);

const DEFAULT_LABELS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]: "When are you available to meet?",
  [MatchingQuestionType.RolePreference]: "What role do you prefer?",
  [MatchingQuestionType.Personality]: "What is your personality type?",
  [MatchingQuestionType.PreferredTeammates]:
    "Are there classmates you'd like to work with?",
  [MatchingQuestionType.CustomText]: "New question",
};

const DEFAULT_DESCRIPTIONS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]:
    "Select all time slots that work for you.",
  [MatchingQuestionType.RolePreference]:
    "Select the role you would most like to take on.",
  [MatchingQuestionType.Personality]:
    "Select the option that best describes you.",
  [MatchingQuestionType.PreferredTeammates]: "",
  [MatchingQuestionType.CustomText]: "",
};

// ── Survey preview ─────────────────────────────────────────────────────────────

const PREVIEW_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PREVIEW_SLOTS = ["Morning", "Afternoon", "Evening"];

function SurveyPreview({
  questions,
}: { questions: Partial<MatchingQuestion>[] }) {
  return (
    <Box>
      {questions.map((q, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography fontWeight={600} mb={0.5}>
            {q.label || "(no label)"}
            <Typography component="span" color="error" ml={0.5}>
              *
            </Typography>
          </Typography>
          {q.description && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              {q.description}
            </Typography>
          )}
          {/* Show a representative sample of each input type */}
          {q.questionType === MatchingQuestionType.TimeAvailability && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${PREVIEW_DAYS.length}, 1fr)`,
                gap: 0.5,
              }}
            >
              {PREVIEW_DAYS.map((d) => (
                <Typography
                  key={d}
                  variant="caption"
                  align="center"
                  fontWeight={600}
                  sx={{ py: 0.5 }}
                >
                  {d}
                </Typography>
              ))}
              {PREVIEW_SLOTS.map((slot) =>
                PREVIEW_DAYS.map((d) => (
                  <Chip
                    key={`${d}-${slot}`}
                    label={slot.substring(0, 2)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.65rem", height: 22 }}
                  />
                )),
              )}
            </Box>
          )}
          {q.questionType === MatchingQuestionType.RolePreference && (
            <Box display="flex" gap={1} flexWrap="wrap">
              {[
                "Project Manager",
                "Designer",
                "Developer",
                "Researcher",
                "Presenter",
              ].map((r) => (
                <Chip key={r} label={r} variant="outlined" size="small" />
              ))}
            </Box>
          )}
          {q.questionType === MatchingQuestionType.Personality && (
            <Box display="flex" gap={1} flexWrap="wrap">
              {["INTJ", "INTP", "ENTJ", "ENTP", "INFJ", "INFP"].map((t) => (
                <Chip key={t} label={t} variant="outlined" size="small" />
              ))}
              <Typography variant="caption" color="text.secondary">
                …and more
              </Typography>
            </Box>
          )}
          {q.questionType === MatchingQuestionType.PreferredTeammates && (
            <TextField
              size="small"
              fullWidth
              placeholder="Type classmate names separated by commas…"
              disabled
              variant="outlined"
            />
          )}
          {q.questionType === MatchingQuestionType.CustomText && (
            <TextField
              size="small"
              fullWidth
              placeholder="Your answer…"
              disabled
              multiline
              rows={2}
              variant="outlined"
            />
          )}
        </Paper>
      ))}
    </Box>
  );
}

// ── Draft types ────────────────────────────────────────────────────────────────

type DraftConfig = {
  name: string;
  description: string;
  isTemplate: boolean;
  questions: Partial<MatchingQuestion>[];
};

function defaultConfig(): DraftConfig {
  return {
    name: "Team Matching Config",
    description: "",
    isTemplate: false,
    questions: [
      {
        questionType: MatchingQuestionType.TimeAvailability,
        label: DEFAULT_LABELS[MatchingQuestionType.TimeAvailability],
        description:
          DEFAULT_DESCRIPTIONS[MatchingQuestionType.TimeAvailability],
        weight: 7,
        order: 0,
        options: [],
        required: true,
        similarityPreference:
          QUESTION_TYPE_DEFAULT_AFFINITY[MatchingQuestionType.TimeAvailability],
      },
      {
        questionType: MatchingQuestionType.RolePreference,
        label: DEFAULT_LABELS[MatchingQuestionType.RolePreference],
        description: DEFAULT_DESCRIPTIONS[MatchingQuestionType.RolePreference],
        weight: 5,
        order: 1,
        options: [],
        required: true,
        similarityPreference:
          QUESTION_TYPE_DEFAULT_AFFINITY[MatchingQuestionType.RolePreference],
      },
    ],
  };
}

function configFromExisting(config: MatchingConfig): DraftConfig {
  return {
    name: config.name,
    description: config.description,
    isTemplate: config.isTemplate,
    questions: config.questions.map((q) => ({ ...q })),
  };
}

// ── Affinity slider label ──────────────────────────────────────────────────────

function affinityLabel(val: number): string {
  if (val <= -0.75) return "Maximize diversity";
  if (val <= -0.25) return "Prefer diverse";
  if (val < 0.25) return "Balanced";
  if (val < 0.75) return "Prefer similar";
  return "Maximize similarity";
}

// ── Props & component ──────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  existing?: MatchingConfig;
  onClose: () => void;
  onSave: (draft: DraftConfig) => void;
  saving?: boolean;
}

export function MatchingConfigEditor({
  open,
  existing,
  onClose,
  onSave,
  saving,
}: Props) {
  const [draft, setDraft] = useState<DraftConfig>(
    existing ? configFromExisting(existing) : defaultConfig(),
  );
  const [addPopoverAnchor, setAddPopoverAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  // DnD state
  const dragIndex = useRef<number | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(existing ? configFromExisting(existing) : defaultConfig());
    }
  }, [open, existing]);

  const set = <K extends keyof DraftConfig>(key: K, value: DraftConfig[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const setQuestion = (i: number, patch: Partial<MatchingQuestion>) =>
    setDraft((d) => {
      const questions = [...d.questions];
      questions[i] = { ...questions[i], ...patch };
      return { ...d, questions };
    });

  const addQuestion = (type: MatchingQuestionType) => {
    setAddPopoverAnchor(null);
    setDraft((d) => ({
      ...d,
      questions: [
        ...d.questions,
        {
          questionType: type,
          label: DEFAULT_LABELS[type],
          description: DEFAULT_DESCRIPTIONS[type],
          weight: 5,
          order: d.questions.length,
          options: [],
          required: true,
          similarityPreference: QUESTION_TYPE_DEFAULT_AFFINITY[type],
        },
      ],
    }));
  };

  const removeQuestion = (i: number) =>
    setDraft((d) => ({
      ...d,
      questions: d.questions.filter((_, idx) => idx !== i),
    }));

  const moveQuestion = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const questions = [...d.questions];
      const target = i + dir;
      if (target < 0 || target >= questions.length) return d;
      [questions[i], questions[target]] = [questions[target], questions[i]];
      return { ...d, questions };
    });

  // Which singleton types are already in the config?
  const usedSingletons = new Set(
    draft.questions
      .map((q) => q.questionType as MatchingQuestionType)
      .filter((t) => SINGLETON_TYPES.has(t)),
  );

  // Available types for the Add popover (hide already-used singletons)
  const availableTypes = Object.values(MatchingQuestionType).filter(
    (t) => !SINGLETON_TYPES.has(t) || !usedSingletons.has(t),
  );

  // HTML5 DnD handlers
  const handleDragStart = (i: number) => {
    dragIndex.current = i;
  };

  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setDraft((d) => {
      const questions = [...d.questions];
      const [removed] = questions.splice(from, 1);
      questions.splice(i, 0, removed);
      dragIndex.current = i;
      return { ...d, questions };
    });
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {existing ? "Edit Matching Config" : "New Matching Config"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Config name + template toggle — inline */}
            <Box display="flex" alignItems="flex-start" gap={2}>
              <TextField
                label="Config Name"
                fullWidth
                value={draft.name}
                onChange={(e) => set("name", e.target.value)}
                required
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.isTemplate}
                    onChange={(e) => set("isTemplate", e.target.checked)}
                  />
                }
                label="Save as reusable template"
                sx={{ whiteSpace: "nowrap", mt: 1 }}
              />
            </Box>

            {/* Description — only shown when saving as template */}
            {draft.isTemplate && (
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={draft.description}
                onChange={(e) => set("description", e.target.value)}
                helperText="Describe when to use this template so you can find it later."
              />
            )}

            {/* Survey questions */}
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Box>
                  <Typography variant="subtitle2">Survey Questions</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Drag to reorder. Set each question&apos;s team affinity
                    (diverse vs similar) individually.
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Preview />}
                    onClick={() => setPreviewOpen(true)}
                    disabled={draft.questions.length === 0}
                  >
                    Preview Survey
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={(e) => setAddPopoverAnchor(e.currentTarget)}
                    disabled={
                      availableTypes.length === 0 ||
                      draft.questions.length >= MAX_CRITERIA
                    }
                  >
                    Add Question
                  </Button>
                  {draft.questions.length >= MAX_CRITERIA && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      Maximum 4 criteria
                    </Typography>
                  )}
                </Box>
              </Box>

              {draft.questions.length === 0 && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  align="center"
                  py={3}
                >
                  No questions yet. Add at least one question.
                </Typography>
              )}

              {draft.questions.map((q, i) => {
                const qType = (q.questionType ??
                  MatchingQuestionType.CustomText) as MatchingQuestionType;
                const chipColor = QUESTION_TYPE_CHIP_COLOR[qType];
                const affinity = q.similarityPreference ?? 0;
                return (
                  <Box
                    key={i}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 1,
                      cursor: "grab",
                      "&:active": { cursor: "grabbing" },
                      bgcolor: "background.paper",
                      overflow: "hidden",
                    }}
                  >
                    {/* ── Header row: drag handle, type badge, reorder arrows, delete ── */}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: "action.hover",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <DragIndicator
                        fontSize="small"
                        sx={{ color: "text.disabled", flexShrink: 0 }}
                      />
                      <Chip
                        label={QUESTION_TYPE_LABELS[qType]}
                        size="small"
                        color={chipColor}
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexGrow: 1, ml: 0.5 }}
                      >
                        {QUESTION_TYPE_SUBLABELS[qType]}
                      </Typography>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Move up"
                            disabled={i === 0}
                            onClick={() => moveQuestion(i, -1)}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Move down"
                            disabled={i === draft.questions.length - 1}
                            onClick={() => moveQuestion(i, 1)}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove question">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Delete question"
                          onClick={() => removeQuestion(i)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* ── Body: label, description, affinity ── */}
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        {/* Question label */}
                        <TextField
                          size="small"
                          label="Question Label"
                          fullWidth
                          value={q.label ?? ""}
                          onChange={(e) =>
                            setQuestion(i, { label: e.target.value })
                          }
                        />

                        {/* Description */}
                        <TextField
                          size="small"
                          label="Description (optional)"
                          fullWidth
                          value={q.description ?? ""}
                          onChange={(e) =>
                            setQuestion(i, { description: e.target.value })
                          }
                        />

                        {/* Per-question affinity slider (hidden for preferred teammates) */}
                        {qType !== MatchingQuestionType.PreferredTeammates &&
                          qType !== MatchingQuestionType.CustomText && (
                            <Box>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                mb={0}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Team affinity
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {affinityLabel(affinity)}
                                </Typography>
                              </Box>
                              <Slider
                                value={affinity}
                                min={-1}
                                max={1}
                                step={0.25}
                                marks={[
                                  { value: -1, label: "Diverse" },
                                  { value: 0, label: "Balanced" },
                                  { value: 1, label: "Similar" },
                                ]}
                                onChange={(_e, v) =>
                                  setQuestion(i, {
                                    similarityPreference: v as number,
                                  })
                                }
                                size="small"
                                aria-label={`team affinity for question ${
                                  i + 1
                                }`}
                                sx={{
                                  // Prevent edge mark labels from being clipped
                                  // by the parent card's overflow:hidden
                                  "& .MuiSlider-markLabel[data-index='0']": {
                                    transform: "translateX(0%)",
                                  },
                                  "& .MuiSlider-markLabel[data-index='2']": {
                                    transform: "translateX(-100%)",
                                  },
                                }}
                              />
                            </Box>
                          )}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => onSave(draft)}
            disabled={!draft.name || draft.questions.length === 0 || saving}
          >
            {saving ? "Saving…" : "Save Config"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Question popover */}
      <Popover
        open={Boolean(addPopoverAnchor)}
        anchorEl={addPopoverAnchor}
        onClose={() => setAddPopoverAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box p={1} minWidth={260}>
          <Typography
            variant="caption"
            color="text.secondary"
            px={1}
            display="block"
            mb={0.5}
          >
            Choose a question type
          </Typography>
          {availableTypes.map((type) => (
            <Box
              key={type}
              onClick={() => addQuestion(type)}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {QUESTION_TYPE_LABELS[type]}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {QUESTION_TYPE_SUBLABELS[type]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Survey preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Survey Preview</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This is what students will see when they receive the survey.
          </Typography>
          <SurveyPreview questions={draft.questions} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export type { DraftConfig };
